import { ENV_CONFIG } from '../../config/environment'
import {
  ACCOUNT_BANNED_CODE,
  AUTH_BANNED_EVENT,
  AuthError,
  createAccountBannedError,
  LOGIN_REQUIRED_MESSAGE,
  REFRESH_TRANSIENT_ERROR_CODE,
  type RefreshResult,
  SESSION_EXPIRED_MESSAGE,
} from './types'

export const API_BASE_URL = ENV_CONFIG.apiBaseUrl

type RefreshFn = () => Promise<RefreshResult>
type ClearUserFn = () => void

/**
 * レスポンスを JSON としてパースする。
 * 204 No Content や空 body の場合は undefined を返す。
 */
async function parseJsonResponse<T>(response: Response): Promise<T> {
  if (response.status === 204) {
    return undefined as T
  }
  // 空 body の JSON パース失敗を undefined として扱う
  return response.json().catch(() => undefined as T)
}

class AuthApiClientClass {
  private refreshAccessToken: RefreshFn = () => Promise.resolve('transient-error')
  private clearUser: ClearUserFn = () => {}

  init(refreshAccessToken: RefreshFn, clearUser: ClearUserFn): void {
    this.refreshAccessToken = refreshAccessToken
    this.clearUser = clearUser
  }

  getCsrfToken(): string | null {
    const match = document.cookie.match(/(?:^|;\s*)xrift-csrf-token=([^;]*)/)
    return match ? decodeURIComponent(match[1]) : null
  }

  async request<T>(endpoint: string, options: RequestInit = {}): Promise<T> {
    return this.apiRequest<T>(endpoint, options, false)
  }

  async publicRequest<T>(endpoint: string, options: RequestInit = {}): Promise<T> {
    const url = `${API_BASE_URL}${endpoint}`
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      ...(options.headers as Record<string, string>),
    }

    const response = await fetch(url, {
      ...options,
      headers,
      credentials: 'include',
    })

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ message: 'Network error' }))
      throw new AuthError({
        message: errorData.error || errorData.message || `API error: ${response.status}`,
        code: response.status.toString(),
      })
    }

    return parseJsonResponse<T>(response)
  }

  private async apiRequest<T>(
    endpoint: string,
    options: RequestInit = {},
    isRetry = false,
  ): Promise<T> {
    const url = `${API_BASE_URL}${endpoint}`
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      ...(options.headers as Record<string, string>),
    }

    // 非 GET リクエストに CSRF トークンを付与
    const method = (options.method || 'GET').toUpperCase()
    if (method !== 'GET') {
      const csrfToken = this.getCsrfToken()
      if (csrfToken) {
        headers['X-CSRF-Token'] = csrfToken
      }
    }

    const response = await fetch(url, {
      ...options,
      headers,
      credentials: 'include',
    })

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ message: 'Network error' }))

      // 401エラー時にリフレッシュを試行（認証エンドポイント以外）
      const isAuthEndpoint =
        endpoint.includes('/auth/login') ||
        endpoint.includes('/auth/register') ||
        endpoint.includes('/auth/refresh')

      // BAN時は即座にログアウト + Reactに通知
      if (response.status === 403 && errorData.code === ACCOUNT_BANNED_CODE) {
        this.clearUser()
        window.dispatchEvent(new CustomEvent(AUTH_BANNED_EVENT))
        throw createAccountBannedError()
      }

      if (response.status === 401 && !isAuthEndpoint && !isRetry) {
        const refreshResult = await this.refreshAccessToken()

        // リフレッシュ成功時のみリトライ
        if (refreshResult === 'refreshed') return this.apiRequest<T>(endpoint, options, true)

        // BAN は refreshAccessToken 内で clearUser と auth:banned の通知まで済んでいる
        if (refreshResult === 'banned') throw createAccountBannedError()

        // そもそもログイン中でない（キャッシュユーザー無し）。サーバーには問い合わせていないので
        // 「期限切れ」ではなく「ログインが必要」と伝える
        if (refreshResult === 'no-session') {
          throw new AuthError({ message: LOGIN_REQUIRED_MESSAGE, code: '401' })
        }

        // サーバーが確定的な認証失効（リフレッシュトークンが無効/期限切れ）と判定した時だけログアウト。
        // この経路では refreshAccessToken 内で clearUser 済みだが、呼び出し元の意図を明示する。
        if (refreshResult === 'unauthorized') {
          this.clearUser()
          throw new AuthError({ message: SESSION_EXPIRED_MESSAGE, code: '401' })
        }

        // 一過性エラー（5xx・ネットワーク瞬断等）では認証状態を保持し、リトライ可能なエラーを投げる。
        // ここでログアウトすると、トークン期限切れの瞬間の一時的な通信不調で
        // 認証が失われ、インスタンス内でゲスト降格などの不具合を引き起こすため。
        throw new AuthError({
          message: '通信エラーにより認証を更新できませんでした。時間をおいて再度お試しください。',
          code: REFRESH_TRANSIENT_ERROR_CODE,
        })
      }

      throw new AuthError({
        message: errorData.error || errorData.message || 'API request failed',
        code: response.status.toString(),
      })
    }

    return parseJsonResponse<T>(response)
  }

  async wrapAuthError<T>(fn: () => Promise<T>, fallbackMessage: string): Promise<T> {
    try {
      return await fn()
    } catch (error) {
      if (error instanceof AuthError) throw error
      throw new AuthError({ message: fallbackMessage })
    }
  }
}

export const AuthApiClient = new AuthApiClientClass()
